import { create } from 'zustand';
import {
  type QueueEntry,
  type QueuedOp,
  enqueueInto,
  load,
  save,
  prune,
  remapIds,
  dropOrphansOf,
} from '../lib/offline/queue.js';

/**
 * L'état React de la file hors-ligne. Toute la logique qui décide quelque chose vit
 * dans `lib/offline/queue.ts` (pure, testée) ; ce store n'est que le porteur d'état
 * et le point de persistance. Voir [ADR 0010](../../../docs/adr/0010-ecritures-offline-file-de-rejeu.md).
 *
 * Il est lu hors de React par le drain et par les stores métier, via `getState()` —
 * le même motif que `useToast` dans `stores/tasks.ts`.
 */
interface OfflineState {
  pending: QueueEntry[];
  /** Vrai pendant un drain : empêche deux rejeux concurrents de doubler les requêtes. */
  draining: boolean;
  enqueue: (op: QueuedOp) => void;
  setPending: (next: QueueEntry[]) => void;
  setDraining: (v: boolean) => void;
  remap: (map: ReadonlyMap<string, string>) => void;
  dropOrphans: (tempId: string) => void;
  /** Jette les entrées trop vieilles pour être rejouées, et rend ce qui a été jeté. */
  pruneExpired: () => QueueEntry[];
}

function persist(next: QueueEntry[]): QueueEntry[] {
  save(next);
  return next;
}

export const useOffline = create<OfflineState>((set, get) => ({
  pending: load(),
  draining: false,
  enqueue: (op) => set({ pending: persist(enqueueInto(get().pending, op)) }),
  setPending: (next) => set({ pending: persist(next) }),
  setDraining: (draining) => set({ draining }),
  remap: (map) => set({ pending: persist(remapIds(get().pending, map)) }),
  dropOrphans: (tempId) => set({ pending: persist(dropOrphansOf(get().pending, tempId)) }),
  pruneExpired: () => {
    const { kept, expired } = prune(get().pending);
    if (expired.length > 0) set({ pending: persist(kept) });
    return expired;
  },
}));

/** Y a-t-il une écriture non confirmée ? Ce que `tasks` consulte avant de se recharger. */
export function hasPending(): boolean {
  return useOffline.getState().pending.length > 0;
}
